import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Timer } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

const Countdown = ({ endsAt }) => {
  const calc = () => {
    const diff = new Date(endsAt).getTime() - Date.now();
    if (isNaN(diff) || diff <= 0) return null;
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  };
  const [left, setLeft] = useState(calc);

  useEffect(() => {
    const t = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(t);
  }, [endsAt]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!left) return null;

  const units = [
    { v: left.d, l: "Hari" },
    { v: left.h, l: "Jam" },
    { v: left.m, l: "Menit" },
    { v: left.s, l: "Detik" },
  ];

  return (
    <div className="flex items-center gap-3 mt-5 flex-wrap" data-testid="promo-countdown">
      <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-[#EACB6B]">
        <Timer className="w-3.5 h-3.5" /> Berakhir dalam
      </span>
      <div className="flex items-center gap-2">
        {units.map((u) => (
          <span key={u.l} className="text-center border border-[#EACB6B]/30 bg-[#EACB6B]/10 px-2 py-1.5 min-w-[44px] sm:min-w-[52px]">
            <span className="block font-display text-lg leading-none text-[#EACB6B]" data-testid={`countdown-${u.l.toLowerCase()}`}>{pad(u.v)}</span>
            <span className="block font-mono text-[7px] uppercase tracking-[0.2em] text-[#FBF7EE]/50 mt-1">{u.l}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const PromoBanner = ({ products }) => {
  const featured = products.filter((p) => p.featured);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (featured.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % featured.length), 6000);
    return () => clearInterval(t);
  }, [featured.length]);

  if (featured.length === 0) return null;
  const promo = featured[idx % featured.length];

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-7xl mx-auto px-6 md:px-12 -mb-6 relative z-10"
      data-testid="promo-banner"
    >
      <div className="relative overflow-hidden bg-[#211B10] text-[#FBF7EE] border border-[#A67C1E]/40">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#A67C1E]/20 blur-[100px] pointer-events-none" aria-hidden="true" />
        <AnimatePresence mode="wait">
          <motion.div
            key={promo.product_id}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to={`/p/${promo.slug || promo.product_id}`}
              data-testid={`promo-banner-link-${promo.product_id}`}
              className="group flex flex-col md:flex-row md:items-center gap-6 md:gap-10 p-8 md:p-10"
            >
              {promo.image_url && (
                <img
                  src={promo.image_url}
                  alt={promo.name}
                  className="w-16 h-16 md:w-28 md:h-28 object-cover border border-[#A67C1E]/40 shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#EACB6B] mb-3">
                  <Sparkles className="w-3.5 h-3.5" /> {promo.coming_soon ? "Open Pre-Order" : "Sorotan Minggu Ini"}
                </p>
                <p className="font-display italic text-2xl md:text-3xl tracking-tight leading-snug" data-testid="promo-banner-text">
                  {promo.promo_text || promo.tagline || promo.name}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FBF7EE]/50 mt-3">{promo.name}</p>
                {promo.promo_ends_at && <Countdown endsAt={promo.promo_ends_at} />}
              </div>
              <span className="inline-flex items-center gap-2 self-start md:self-center shrink-0 bg-[#A67C1E] text-[#FBF7EE] text-xs font-medium uppercase tracking-[0.15em] px-6 py-3.5 group-hover:bg-[#EACB6B] group-hover:text-[#211B10] transition-colors duration-300">
                {promo.coming_soon ? "Pesan PO Sekarang" : "Lihat Penawaran"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Link>
          </motion.div>
        </AnimatePresence>
        {featured.length > 1 && (
          <div className="absolute bottom-4 left-8 md:left-10 flex items-center gap-2" data-testid="promo-banner-dots">
            {featured.map((p, i) => (
              <button
                key={p.product_id}
                data-testid={`promo-dot-${i}`}
                aria-label={`Sorotan ${i + 1}`}
                onClick={() => setIdx(i)}
                className={`h-1 transition-all duration-300 ${i === idx % featured.length ? "w-6 bg-[#EACB6B]" : "w-2.5 bg-[#FBF7EE]/30 hover:bg-[#FBF7EE]/60"}`}
              />
            ))}
          </div>
        )}
      </div>
    </motion.section>
  );
};
